import React from "react"
import { Script, useStaticQuery, graphql } from "gatsby"
import styled from "styled-components"
import { rem } from "polished"
import {
  Link,
  ImagesCarousel,
  Map,
} from "@outdoormap/gatsby-plugin-brand-sites"

import SitesCarousel from "../sites-carousel"
import PostsCarousel from "../posts-carousel"
import Styled from "../styled"
import { Heading1, Heading3 } from "../styled/heading"
import { Label3 } from "../styled/label"
import { ParagraphLarge } from "../styled/paragraph"
import { anchorStyles } from "../styled/anchor"
import { ReactComponent as SvgArrowRight } from "../../assets/images/icons/arrow-right.svg"
import colors from "../../theme/colors"

const Post = ({ data, other, otherHeading, otherCta, otherCtaUrl }) => {
  const { translations } = useStaticQuery(graphql`
    query {
      translations: translationsJson {
        global {
          mentionedInThisPost
          documents
        }
      }
    }
  `)

  if (!data) return null

  const anySites = data.sites && !!data.sites.length
  const mapQuery = anySites
    ? data.sites.map((s) => `site=${s.id}`).join(`&`)
    : null

  const bookSpotioScriptLoad = () => {
    window.outventuresSettings = [
      {
        base_url: `https://app.bookspot.io/widget/`,
        app_id: `0d4b7582e49b894c3e18b39cd757aff2d7eada035f53f14c3be72f0c90ee66d7`,
        widget_id: `t6xfooHIyxvG3Id`,
        floating: false,
        btn_text: {
          sv: `Boka din biljett här`,
          en: `Book your ticket here`,
        },
        btn_style: {
          size: `large`,
          bg: colors.green,
          color: colors.white,
        },
        list_view: `calendar`,
        product_types: [`products`, `vouchers`],
        categories: [1790],
      },
    ]

    window.lw?.()
  }

  return (
    <React.Fragment>
      <Article>
        <Header>
          {data.date && (
            <Date
              datetime={data.date}
              dangerouslySetInnerHTML={{ __html: data.date }}
            />
          )}

          {data.title && (
            <Title dangerouslySetInnerHTML={{ __html: data.title }} />
          )}

          {data.tagline && (
            <Tagline dangerouslySetInnerHTML={{ __html: data.tagline }} />
          )}
        </Header>

        {data.images && !!data.images.length && (
          <Images>
            <div>
              <ImagesCarousel data={data.images} title={data.title} />
            </div>
          </Images>
        )}

        {data.id == `53337` && (
          <BookSpotio>
            <div>
              <span id="ovbs-scriptt6xfooHIyxvG3Id" />
            </div>

            <Script id="bookspotio-t6xfooHIyxvG3Id">
              {`window.outventuresSettings = [];`}
            </Script>

            <Script
              src="https://app.bookspot.io/widget/widget.js"
              onLoad={bookSpotioScriptLoad}
            />
          </BookSpotio>
        )}

        {data.body && (
          <Content dangerouslySetInnerHTML={{ __html: data.body }} />
        )}

        {(!!(data.files && data.files.length) ||
          !!(data.link && data.linkText)) && (
          <Documents>
            <Heading3
              as="h2"
              dangerouslySetInnerHTML={{
                __html: translations.global.documents,
              }}
            />

            <Styled>
              <ul>
                {data.files.map((pdf, i) => (
                  <li key={i}>
                    <Link to={pdf.url} target="_blank">
                      {pdf.text}
                    </Link>
                  </li>
                ))}

                {data.link && data.linkText && (
                  <li>
                    <Link to={data.link} target="_blank">
                      {data.linkText}
                    </Link>
                  </li>
                )}
              </ul>
            </Styled>
          </Documents>
        )}

        {mapQuery && (
          <MapWrap>
            <Map
              query={mapQuery}
              menu="fullscreen"
              scrollZoom="false"
              styleControl="fullscreen"
              zoomControl="fullscreen"
              measureControl="fullscreen"
              locationControl="fullscreen"
              printControl="fullscreen"
              trailControl="false"
            />
          </MapWrap>
        )}

        {anySites && (
          <Sites>
            <div>
              <Label3
                as="h2"
                dangerouslySetInnerHTML={{
                  __html: `${translations.global.mentionedInThisPost}…`,
                }}
              />

              <SitesCarousel visibleSlides={2} data={data.sites} />
            </div>
          </Sites>
        )}
      </Article>

      {other && !!other.nodes.length && (
        <Other>
          <div>
            {otherHeading && (
              <Label3
                as="h2"
                dangerouslySetInnerHTML={{ __html: otherHeading }}
              />
            )}

            <PostsCarousel visibleSlides={3} data={other.nodes} />

            {otherCta && otherCtaUrl && (
              <OtherCta>
                <Link to={otherCtaUrl}>
                  <span dangerouslySetInnerHTML={{ __html: otherCta }} />

                  <SvgArrowRight aria-hidden="true" />
                </Link>
              </OtherCta>
            )}
          </div>
        </Other>
      )}
    </React.Fragment>
  )
}

export default Post

const Article = styled.article`
  margin-bottom: ${rem(40)};
  min-width: ${rem(290)};
`

const Header = styled.header`
  ${({ theme }) => theme.grid.container(`xnarrow`)}

  margin-bottom: ${rem(50)};
  padding-top: ${rem(20)};
  text-align: center;

  @media ${({ theme }) => theme.mq.mediumDown} {
    margin-bottom: ${rem(20)};
  }
`

const Documents = styled.section`
  ${({ theme }) => theme.grid.container(`xnarrow`)}

  margin-top: ${rem(40)};
  padding-top: ${rem(40)};
  border-top: 1px solid ${({ theme }) => theme.colors.greenAlt};

  ${Styled} {
    margin-top: ${rem(20)};
  }
`

const Date = styled.time`
  margin-bottom: 0.875em;
  display: block;
  font-size: 1.125em;
  color: ${({ theme }) => theme.colors.metallic};
`

const Title = styled(Heading1)``

const Tagline = styled(ParagraphLarge)`
  margin-top: 0.75em;
`

const Images = styled.div`
  ${({ theme }) => theme.grid.container(`narrow`)}

  margin-bottom: ${rem(50)};

  @media ${({ theme }) => theme.mq.mediumDown} {
    margin-bottom: ${rem(20)};
    padding-left: 0;
    padding-right: 0;
  }

  > div {
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    border-radius: ${rem(10)};

    @media ${({ theme }) => theme.mq.largeDown} {
      border-radius: 0;
    }
  }

  figure {
    @media ${({ theme }) => theme.mq.largeDown} {
      height: 100%;
    }
  }
`

const BookSpotio = styled.div`
  ${({ theme }) => theme.grid.container(`xnarrow`)}

  margin-bottom: ${rem(50)};

  & > div {
    padding-top: ${rem(30)};
    padding-bottom: ${rem(30)};
    display: flex;
    justify-content: center;
    background-color: ${({ theme }) => theme.colors.cream};
  }
`

const Content = styled(Styled)`
  ${({ theme }) => theme.grid.container(`xnarrow`)}
`

const MapWrap = styled.section`
  ${({ theme }) => theme.grid.container(`xnarrow`)}

  height: 80vh;
  min-height: ${rem(120)};
  max-height: ${rem(300)};
  z-index: 10;
  position: relative;
  margin-top: ${rem(40)};
  * {
    border-radius: ${rem(15)};
  }
`

const Sites = styled.footer`
  ${({ theme }) => theme.grid.container(`xnarrow`)}

  > div {
    margin-top: ${rem(40)};
    padding-top: ${rem(40)};
    border-top: 1px solid ${({ theme }) => theme.colors.cream};
  }

  ${Label3} {
    margin-bottom: ${rem(20)};
  }
`

const Other = styled.section`
  background-color: ${({ theme }) => theme.colors.cream};

  > div {
    ${({ theme }) => theme.grid.container()}

    padding-top: ${rem(50)};
    padding-bottom: ${rem(50)};

    @media ${({ theme }) => theme.mq.mediumDown} {
      margin-top: ${rem(20)};
      padding-top: ${rem(20)};
      padding-bottom: ${rem(20)};
    }
  }

  ${Label3} {
    margin-bottom: ${rem(20)};
    color: ${({ theme }) => theme.colors.metallic};
  }
`

const OtherCta = styled.footer`
  margin-top: ${rem(30)};

  a {
    ${anchorStyles}

    &:hover {
      svg {
        transform: translateX(0.3em);
      }
    }

    span {
      margin-right: 0.4em;
    }

    svg {
      width: 1.4em;
      height: 1.4em;
      vertical-align: middle;
      transition: transform 0.2s ${({ theme }) => theme.easings.default};
    }
  }
`
